import React, { useEffect, useRef, useState } from 'react';
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { auth } from './firebase';

function obterMensagemErroMfa(error) {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (error?.code === 'auth/captcha-check-failed') {
    return `O domínio ${hostname || 'deste painel'} ainda não está autorizado no Firebase para SMS. Vá em Authentication > Configurações > Domínios autorizados e adicione este domínio.`;
  }

  if (error?.code === 'auth/invalid-phone-number') {
    return 'O celular cadastrado para esta conta está em formato inválido.';
  }

  if (error?.code === 'auth/too-many-requests') {
    return 'Muitas tentativas seguidas. Aguarde alguns minutos e tente novamente.';
  }

  return 'Não foi possível iniciar a confirmação em duas etapas agora.';
}

function Login() {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [codigoMfa, setCodigoMfa] = useState('');
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaVerificationId, setMfaVerificationId] = useState('');
  const [mfaDestino, setMfaDestino] = useState('');
  const [carregando, setCarregando] = useState(false);
  const recaptchaRef = useRef(null);
  const recaptchaContainerId = 'mfa-login-recaptcha';

  useEffect(() => () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.clear();
      recaptchaRef.current = null;
    }
  }, []);

  async function prepararRecaptcha() {
    if (recaptchaRef.current) {
      return recaptchaRef.current;
    }

    const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
      size: 'invisible',
    });

    await verifier.render();
    recaptchaRef.current = verifier;
    return verifier;
  }

  function limparFluxoMfa() {
    setMfaResolver(null);
    setMfaVerificationId('');
    setMfaDestino('');
    setCodigoMfa('');
  }

  async function iniciarDesafioMfa(error) {
    const resolver = getMultiFactorResolver(auth, error);
    const hint = resolver.hints.find((item) => item.factorId === PhoneMultiFactorGenerator.FACTOR_ID) || resolver.hints[0];

    if (!hint) {
      throw new Error('Nenhum segundo fator compatível foi encontrado para esta conta.');
    }

    const verifier = await prepararRecaptcha();
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber({
      multiFactorHint: hint,
      session: resolver.session,
    }, verifier);

    setMfaResolver(resolver);
    setMfaVerificationId(verificationId);
    setMfaDestino(hint.phoneNumber || 'celular cadastrado');
    setErro('');
  }

  async function confirmarCodigoMfa() {
    if (!mfaResolver || !mfaVerificationId || codigoMfa.trim().length < 6) {
      setErro('Digite o código de segurança enviado por SMS.');
      return;
    }

    const credencial = PhoneAuthProvider.credential(mfaVerificationId, codigoMfa.trim());
    const assertion = PhoneMultiFactorGenerator.assertion(credencial);
    await mfaResolver.resolveSignIn(assertion);
    limparFluxoMfa();
  }

  const fazerLogin = async (e) => {
    e.preventDefault();
    setErro('');
    setCarregando(true);

    try {
      if (mfaResolver) {
        await confirmarCodigoMfa();
        return;
      }

      const resultado = await signInWithEmailAndPassword(auth, email, senha);
      console.log('Usuário logado:', resultado.user.email);
      limparFluxoMfa();
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        try {
          await iniciarDesafioMfa(error);
        } catch (mfaError) {
          console.error(mfaError);
          setErro(obterMensagemErroMfa(mfaError));
        }
      } else if (mfaResolver) {
        setErro('Código de segurança inválido ou expirado. Gere um novo acesso.');
        console.error(error);
      } else {
        setErro('Acesso negado. Verifique seu e-mail e senha.');
        console.error(error);
      }
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', minHeight: '100vh', width: '100%', fontFamily: 'sans-serif' }}>
      <div style={{
        flex: '1 1 500px',
        background: 'linear-gradient(135deg, #0b132b 0%, #1c2541 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '10%',
        position: 'relative',
      }}>
        <div style={{ position: 'absolute', top: '30px', left: '30px', fontSize: '18px', fontWeight: 'bold', letterSpacing: '2px' }}>
          <span style={{ color: '#2196f3' }}>VMS</span> <span style={{ color: '#ff9800' }}>SISTEMAS E DIVULGAÇÕES</span>
        </div>

        <img src="/logo.jpg" alt="PromoAlert Logo" style={{ maxWidth: '280px', marginBottom: '30px', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }} />

        <h1 style={{ fontSize: '42px', lineHeight: '1.2', marginBottom: '20px', fontWeight: '800' }}>
          O radar definitivo de ofertas para a sua loja.
        </h1>
        <p style={{ fontSize: '18px', color: '#a0aec0', maxWidth: '500px', lineHeight: '1.6' }}>
          Alcance clientes na hora certa, gerencie promoções em lote e aumente suas vendas físicas com a tecnologia do PromoAlert.
        </p>
      </div>

      <div style={{
        flex: '1 1 400px',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 20px',
      }}>
        <div style={{ width: '100%', maxWidth: '380px', backgroundColor: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}>
          <h2 style={{ color: '#1f2937', marginBottom: '30px', textAlign: 'center', fontSize: '24px' }}>Acesso ao Painel</h2>

          <form onSubmit={fazerLogin} style={{ display: 'flex', flexDirection: 'column' }}>
            <label style={{ marginBottom: '8px', fontWeight: 'bold', color: '#4b5563', fontSize: '13px' }}>E-mail corporativo</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={carregando || Boolean(mfaResolver)}
              placeholder="loja@exemplo.com"
              style={{ padding: '12px', marginBottom: '20px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '15px' }}
            />

            <label style={{ marginBottom: '8px', fontWeight: 'bold', color: '#4b5563', fontSize: '13px' }}>Senha de acesso</label>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              required
              disabled={carregando || Boolean(mfaResolver)}
              placeholder="********"
              style={{ padding: '12px', marginBottom: mfaResolver ? '16px' : '30px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '15px' }}
            />

            {mfaResolver && (
              <>
                <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#1e3a8a' }}>
                  Confirmação em duas etapas ativa. Digite o código enviado para {mfaDestino}.
                </div>

                <label style={{ marginBottom: '8px', fontWeight: 'bold', color: '#4b5563', fontSize: '13px' }}>Código de segurança</label>
                <input
                  type="text"
                  value={codigoMfa}
                  onChange={(e) => setCodigoMfa(e.target.value)}
                  required
                  disabled={carregando}
                  placeholder="000000"
                  style={{ padding: '12px', marginBottom: '20px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '15px' }}
                />
              </>
            )}

            <button type="submit" disabled={carregando} style={{ padding: '14px', backgroundColor: '#e53935', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', transition: '0.3s', opacity: carregando ? 0.8 : 1 }}>
              {carregando ? 'Processando...' : (mfaResolver ? 'Confirmar código' : 'Entrar no Sistema')}
            </button>

            {erro && (
              <p style={{ color: '#dc2626', marginTop: '20px', textAlign: 'center', backgroundColor: '#fef2f2', padding: '10px', borderRadius: '6px', fontSize: '13px', border: '1px solid #f87171' }}>
                {erro}
              </p>
            )}

            {mfaResolver && (
              <button
                type="button"
                onClick={() => {
                  limparFluxoMfa();
                  setErro('');
                }}
                disabled={carregando}
                style={{ marginTop: '12px', padding: '12px', backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
              >
                Cancelar confirmação
              </button>
            )}
          </form>

          <div id={recaptchaContainerId} />

          <div style={{ marginTop: '40px', textAlign: 'center', fontSize: '12px', color: '#9ca3af', lineHeight: 1.7 }}>
            <a href="/privacidade" style={{ color: '#0f5ca8', fontWeight: '600', textDecoration: 'none' }}>Política de Privacidade</a>
            <span style={{ padding: '0 7px', color: '#cbd5e1' }}>•</span>
            <a href="/exclusao-de-conta" style={{ color: '#0f5ca8', fontWeight: '600', textDecoration: 'none' }}>Solicitar exclusão de conta</a>
            <div>&copy; {new Date().getFullYear()} VMS Sistemas e Divulgações. Todos os direitos reservados.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
